import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { IoArrowBack, IoCheckmark, IoClose, IoLockClosedOutline } from "react-icons/io5";
import { beginAttioConnect } from "../businessModeService";
import {
  ACTION_ITEMS_SETTINGS_TAB_CONNECT_APPS,
  buildActionItemsListPath,
  buildActionItemsSettingsPath,
  tryCloseExtensionOpenedTab,
} from "./actionItemHelpers";
import BusinessAppSettingsChrome from "./BusinessAppSettingsChrome";
import useSettingsNavCollapsed from "./useSettingsNavCollapsed";
import BusinessAppSettingsNav from "./BusinessAppSettingsNav";
import {
  BUSINESS_OAUTH_POPUP_RESULT_KEY,
  bounceDesktopOauthCallbackIfNeeded,
  completeProviderOAuth,
  getSimpleConnectCopy,
  isPopupOauthReturn,
  renderConnectorSetupIcon,
  resolveBusinessOauthState,
  subscribeBusinessOAuthPopupResult,
  toPopupOauthState,
} from "./shared/businessDigestSetup";

export const ATTIO_SETUP_PATH = "/business/workflows/new/attio";
export const ATTIO_OAUTH_STATE = "business_attio";

/**
 * Attio CRM connector setup — same settings chrome as Zoom/Granola.
 * After OAuth succeeds, shows "Setting up…" and returns to Connectors
 * automatically (no Done click).
 *
 * @example
 * // Route: /business/workflows/new/attio
 * <AttioConnectPanel />
 */
const AttioConnectPanel = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isNavCollapsed, toggleNavCollapsed] = useSettingsNavCollapsed();
  const [connectLoading, setConnectLoading] = useState(false);
  const [oauthExchanging, setOauthExchanging] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState("");
  const oauthPopupRef = useRef(null);
  const didAutoFinishRef = useRef(false);
  const copy = getSimpleConnectCopy("attio");

  const exitToConnectors = () => {
    if (tryCloseExtensionOpenedTab()) {
      return;
    }
    navigate(buildActionItemsSettingsPath({ tab: ACTION_ITEMS_SETTINGS_TAB_CONNECT_APPS }));
  };

  const exitToTasks = () => {
    if (tryCloseExtensionOpenedTab()) {
      return;
    }
    navigate(buildActionItemsListPath({}));
  };

  const handleSettingsTabChange = (tab) => {
    navigate(buildActionItemsSettingsPath({ tab }));
  };

  /**
   * Mark Attio connected and kick off the auto-finish (Setting up…) path.
   *
   * @example
   * finishAfterOauth({ credentialId: "cred_1" });
   * // connected=true, submitting=true → navigates to Connectors
   */
  const finishAfterOauth = (result) => {
    setConnectLoading(false);
    setOauthExchanging(false);
    oauthPopupRef.current = null;
    if (!result) {
      return;
    }
    if (result.cancelled) {
      return;
    }
    if (result.error) {
      setError(result.error);
      setSubmitting(false);
      return;
    }
    if (result.credentialId) {
      setError("");
      setConnected(true);
      setSubmitting(true);
    }
  };

  const connectAttio = async () => {
    try {
      setError("");
      setConnectLoading(true);
      const authUrl = await beginAttioConnect({
        oauthState: toPopupOauthState(ATTIO_OAUTH_STATE),
        redirect: false,
      });
      const popup = window.open(authUrl, "_blank");
      oauthPopupRef.current = popup || null;
    } catch (err) {
      setError(err?.message || "Could not start Attio connection");
      setConnectLoading(false);
    }
  };

  // After OAuth, auto-return to Connectors like digest wizards auto-finish.
  useEffect(() => {
    if (!connected || !submitting || didAutoFinishRef.current || error) {
      return undefined;
    }
    didAutoFinishRef.current = true;
    const timer = window.setTimeout(() => {
      exitToConnectors();
    }, 400);
    return () => window.clearTimeout(timer);
    // exitToConnectors closes over navigate; intentional one-shot finish.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected, submitting, error]);

  // Watch popup localStorage handoff (same pattern as BusinessDigestWizard).
  useEffect(() => {
    if (!connectLoading) {
      return undefined;
    }

    const unsubscribeOauthResult = subscribeBusinessOAuthPopupResult(
      "attio",
      finishAfterOauth
    );

    const pollClosed = window.setInterval(() => {
      const popup = oauthPopupRef.current;
      if (!popup || !popup.closed) {
        return;
      }
      window.clearInterval(pollClosed);
      oauthPopupRef.current = null;
      const raw = window.localStorage.getItem(BUSINESS_OAUTH_POPUP_RESULT_KEY);
      if (raw) {
        try {
          const result = JSON.parse(raw);
          if (result.provider === "attio") {
            window.localStorage.removeItem(BUSINESS_OAUTH_POPUP_RESULT_KEY);
            finishAfterOauth(result);
            return;
          }
        } catch {
          // fall through
        }
      }
      setConnectLoading(false);
    }, 500);

    const giveUpTimeout = window.setTimeout(() => {
      setConnectLoading(false);
    }, 5 * 60 * 1000);

    return () => {
      unsubscribeOauthResult();
      window.clearInterval(pollClosed);
      window.clearTimeout(giveUpTimeout);
    };
  }, [connectLoading]);

  // Complete OAuth when this tab is the callback target (popup or full redirect).
  useEffect(() => {
    const completeOauth = async () => {
      const params = new URLSearchParams(location.search);
      const code = params.get("code");
      const state = params.get("state");
      if (bounceDesktopOauthCallbackIfNeeded()) {
        return;
      }
      const oauthBaseState = resolveBusinessOauthState(state);
      const isPopupReturn = isPopupOauthReturn(state, ATTIO_OAUTH_STATE);
      if (!code || (oauthBaseState !== ATTIO_OAUTH_STATE && !isPopupReturn)) {
        return;
      }

      const handoffToOpener = isPopupReturn;
      setOauthExchanging(true);
      setConnectLoading(true);
      try {
        const { credentialId } = await completeProviderOAuth("attio", code, { state });
        if (handoffToOpener) {
          window.localStorage.setItem(
            BUSINESS_OAUTH_POPUP_RESULT_KEY,
            JSON.stringify({ provider: "attio", credentialId })
          );
          window.history.replaceState({}, "", window.location.pathname);
          setOauthExchanging(false);
          setConnectLoading(false);
          window.close();
          if (!window.closed) {
            window.document.title = "Authentication complete";
            setError(
              "Authentication complete in this tab. You can close this tab and continue in the original window."
            );
          }
          return;
        }
        navigate(ATTIO_SETUP_PATH, { replace: true });
        finishAfterOauth({ credentialId });
      } catch (err) {
        if (handoffToOpener) {
          window.localStorage.setItem(
            BUSINESS_OAUTH_POPUP_RESULT_KEY,
            JSON.stringify({
              provider: "attio",
              error: err?.message || "Attio connection failed",
            })
          );
          window.history.replaceState({}, "", window.location.pathname);
          setOauthExchanging(false);
          setConnectLoading(false);
          window.close();
          if (!window.closed) {
            setError(
              err?.message ||
                "OAuth callback completed in this tab. You can close this tab and return to the original window."
            );
          }
          return;
        }
        setError(err?.message || "Attio connection failed");
        setOauthExchanging(false);
        setConnectLoading(false);
      }
    };
    void completeOauth();
  }, [location.search, navigate]);

  const showSettingUp = connected || submitting;
  const primaryBusy = connectLoading || oauthExchanging || showSettingUp;
  const primaryLabel = connectLoading || oauthExchanging
    ? "Waiting for authorization…"
    : showSettingUp
      ? "Setting up…"
      : copy.connectCta;

  const setupBody = (
    <div className="business-simple-connect">
      <div className="connector-setup-privacy-hint">
        <div className="connector-setup-privacy-hint__line">
          <span
            className="connector-setup-privacy-hint__icon connector-setup-privacy-hint__icon--yes"
            aria-hidden="true"
          >
            <IoCheckmark />
          </span>
          <span>{copy.accessLine}</span>
        </div>
        <div className="connector-setup-privacy-hint__line">
          <span
            className="connector-setup-privacy-hint__icon connector-setup-privacy-hint__icon--no"
            aria-hidden="true"
          >
            <IoClose />
          </span>
          <span>{copy.neverLine}</span>
        </div>
      </div>

      <div className="business-simple-connect__actions">
        {connected ? (
          <div className="business-simple-connect__connected">
            <div className="business-connected-pill">
              <span className="business-connected-pill__dot" aria-hidden="true" />
              Connected
            </div>
          </div>
        ) : null}

        <button
          type="button"
          className="primary-button business-simple-connect__cta"
          onClick={() => void connectAttio()}
          disabled={primaryBusy}
          aria-busy={primaryBusy}
        >
          {primaryBusy ? (
            <span className="business-setup-btn__spinner" aria-hidden="true" />
          ) : (
            <IoLockClosedOutline aria-hidden="true" />
          )}
          <span>{primaryLabel}</span>
        </button>
      </div>
    </div>
  );

  return (
    <div className="business-page-content">
      <div className="business-wizard-wrap business-wizard-wrap--app-settings business-wizard-wrap--connector-setup">
        <div className="business-app-settings-shell">
          <BusinessAppSettingsChrome
            isNavCollapsed={isNavCollapsed}
            onToggleNav={toggleNavCollapsed}
            onBackHome={exitToTasks}
          />
          <div
            className={`business-app-settings-layout${
              isNavCollapsed ? " business-app-settings-layout--nav-collapsed" : ""
            }`}
          >
            <BusinessAppSettingsNav
              activeTab={ACTION_ITEMS_SETTINGS_TAB_CONNECT_APPS}
              onTabChange={handleSettingsTabChange}
              isCollapsed={isNavCollapsed}
            />
            <div className="business-app-settings-main">
              <div className="business-settings-header business-app-settings-topbar">
                <div className="business-wizard-header">
                  <button
                    type="button"
                    className="business-wizard-back-btn"
                    onClick={exitToConnectors}
                    aria-label="Back to connectors"
                  >
                    <IoArrowBack aria-hidden="true" />
                  </button>
                  <div className="business-wizard-header-content">
                    <h2 className="business-wizard-title">Connectors</h2>
                  </div>
                </div>
              </div>
              <div className="business-app-settings">
                <div className="business-app-settings__body">
                  <div className="business-app-settings__panel business-connector-setup">
                    <div className="ds-detail">
                      <div className="ds-detail__header">
                        {renderConnectorSetupIcon("attio", {
                          placement: "header",
                          className: "ds-detail__app-icon",
                        })}
                        <div className="ds-detail__header-text">
                          <h3 className="ds-detail__name">Connect Attio</h3>
                          <span className="ds-detail__header-meta">
                            Write meeting outcomes back to Attio — only when you confirm each
                            write.
                          </span>
                        </div>
                      </div>
                      <div className="ds-detail__body">
                        <div className="ds-detail__settings">
                          {error ? <div className="error-message">{error}</div> : null}
                          {setupBody}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttioConnectPanel;
